import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { type Integration, IntegrationsApi } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ChangeTwilioCredentialModalContent } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationTwilioSettings/ui/ChangeTwilioCredentialModalContent/ChangeTwilioCredentialModalContent'

export class IntegrationTwilioSettingsStore {
  private _loading = false
  private _modalId = nanoid()
  private _switchAccountModalId = nanoid()
  private _keyEditValue = ''
  private _secretEditValue = ''
  private _apiError = ''
  private _saving = false
  private _disposeSaveDisabledReaction: IReactionDisposer | null = null

  constructor(_integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    try {
      await this._entities.numbersStore.fetchNumberVendorIntegrations()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeSaveDisabledReaction?.()
    modalStore.removeModal(this._modalId)
    modalStore.removeModal(this._switchAccountModalId)
  }

  get loading() {
    return this._loading
  }

  get isApiKeyCredentials() {
    return this._entities.featureFlagsStore.twilio_integration_api_key_credentials
  }

  get keyLabel() {
    return this.isApiKeyCredentials ? 'API Key' : 'Account SID'
  }

  get secretLabel() {
    return this.isApiKeyCredentials ? 'API Secret' : 'Auth Token'
  }

  get keyValue() {
    return this._entities.numbersStore.twilioNumberVendorIntegration?.provider_user_id || ''
  }

  get maskedSecretValue() {
    return '•'.repeat(34)
  }

  get keyEditValue() {
    return this._keyEditValue
  }

  get secretEditValue() {
    return this._secretEditValue
  }

  get apiError() {
    return this._apiError
  }

  get saving() {
    return this._saving
  }

  get isSaveDisabled() {
    return !this._keyEditValue.trim() || !this._secretEditValue.trim() || this._saving
  }

  setKeyEditValue = (value: string) => {
    this._keyEditValue = value
    this._apiError = ''
  }

  setSecretEditValue = (value: string) => {
    this._secretEditValue = value
    this._apiError = ''
  }

  openChangeModal = () => {
    runInAction(() => {
      this._keyEditValue = this.keyValue
      this._secretEditValue = ''
      this._apiError = ''
      this._saving = false
    })

    modalStore.addModal({
      id: this._modalId,
      title: 'Update credentials',
      width: 480,
      onClose: this.closeModal,
      ModalContent: ChangeTwilioCredentialModalContent,
      ModalContentProps: { store: this },
      paddingContent: '0 24px 16px',
      primaryAction: {
        text: 'Update',
        onAction: () => this._submit(false),
        disabled: this.isSaveDisabled,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeModal,
      },
    })

    this._disposeSaveDisabledReaction?.()
    this._disposeSaveDisabledReaction = reaction(
      () => this.isSaveDisabled,
      (isDisabled) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = isDisabled
        }
      }
    )
  }

  closeModal = () => {
    this._disposeSaveDisabledReaction?.()
    modalStore.removeModal(this._modalId)
    this._keyEditValue = ''
    this._secretEditValue = ''
    this._apiError = ''
    this._saving = false
  }

  private _submit = async (confirmAccountSwitch: boolean) => {
    runInAction(() => {
      this._saving = true
      this._apiError = ''
    })

    try {
      await this.updateCredentials(confirmAccountSwitch)
      await this._entities.numbersStore.refreshAfterTwilioIntegrationChange()

      modalStore.removeModal(this._switchAccountModalId)
      this.closeModal()
      this._entities.numbersStore.initConnectTwilioModal(false)

      showToast({
        type: 'success',
        title: 'Credentials updated',
      })
    } catch (e) {
      logger.error(e)

      if (this.isAccountSwitchConfirmationRequired(e)) {
        this.openSwitchAccountConfirmModal()
        return
      }
      modalStore.removeModal(this._switchAccountModalId)

      runInAction(() => {
        if (e instanceof AxiosError) {
          this._apiError =
            e.errors.getError('error') ||
            e.errors.getError('api_key') ||
            'Invalid credentials. Please verify them in your Twilio console and try again.'
        }
      })
    } finally {
      runInAction(() => {
        this._saving = false
      })
    }
  }

  isAccountSwitchConfirmationRequired = (e: unknown) => {
    if (!(e instanceof AxiosError) || e.response?.status !== 400) return false

    return e.response.data?.error_code === 'ACCOUNT_SWITCH_CONFIRMATION_REQUIRED'
  }

  openSwitchAccountConfirmModal = () => {
    modalStore.addModal({
      id: this._switchAccountModalId,
      type: ModalTypeList.ALERT,
      title: 'Switch to a different Twilio account?',
      desc: "This will disconnect all existing numbers immediately. They can't be re-enabled, and all conversation history tied to them will no longer be accessible.",
      width: 280,
      primaryAction: {
        text: 'Switch account',
        onAction: () => this._submit(true),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.removeModal(this._switchAccountModalId),
      },
    })
  }

  updateCredentials = (confirmAccountSwitch: boolean) => {
    const key = this._keyEditValue.trim()
    const secret = this._secretEditValue.trim()

    return IntegrationsApi.connectTwilio({
      ...(this.isApiKeyCredentials
        ? { api_key: key, api_key_secret: secret }
        : { account_sid: key, auth_token: secret }),
      confirm_account_switch: confirmAccountSwitch,
    })
  }
}
